"use strict";
const cheerio = require('cheerio');
const numeral = require('numeral');
const moment = require('moment');
const assign = require('lodash/assign');
const isEmpty = require('lodash/isEmpty');
const isNaN = require('lodash/isNaN');
const isNull = require('lodash/isNull');
const isUndefined = require('lodash/isUndefined');
const toLower = require('lodash/toLower');
const get = require('lodash/get');
const isString = require('lodash/isString');
const { formatCurrency, formatHealth } = require('../formatting');
const { isValidEmail } = require('../email');
const { safeParsing } = require('../utils');
const parseAttribute = (span, index, urlId) => {
    const types = {
        TEXT: 'string_attribute',
        NUMBER: 'number_attribute',
        NUMERIC: 'number_attribute',
        DATE: 'simple_date_attribute',
        LIST: 'list_attribute',
        LIFECYCLE: 'lifecycle_attribute',
        MULTIVALUEFIXED: 'string_attribute',
        lifecycle_attribute_total_days: 'lifecycle_attribute_total_days',
        lifecycle_attribute_stage_by_id_total_days: 'lifecycle_attribute_stage_by_id_total_days',
        simple_date_attribute: 'date_attribute',
    };
    const { type, belongs_to: belongsTo, format_pattern: formatPattern, uid, format_type: formatType, field_name: fieldName, stage } = span.attribs;
    return {
        attribute: fieldName,
        field_display_name: fieldName,
        type: types[type] || 'string_attribute',
        ...!isUndefined(stage) && { stage },
        _extra: { format_type: formatType, uid, format_pattern: formatPattern, url_id: urlId, type: belongsTo === 'account' ? 'account' : 'user' },
        index,
    };
};
const parseDateMetric = (span, index, urlId) => ({
    _extra: { field_name: span.attribs.field_name, uid: span.attribs.uid, format_type: 'date', url_id: urlId, format_pattern: span.attribs.format_pattern },
    type: 'date',
    field_display_name: span.attribs.field_name,
    term: span.attribs.field_name,
    index,
});
const parseMetric = (span, index, urlId) => {
    const decoded = decodeURIComponent(span.attribs.q);
    if (decoded) {
        const parsed = JSON.parse(decoded);
        if (parsed) {
            return assign({}, parsed, {
                index,
                _extra: { field_name: 'custom_metrics', uid: span.attribs.uid, format_type: span.attribs.format_type, url_id: urlId, format_pattern: span.attribs.format_pattern },
            });
        }
    }
    return undefined;
};
const parseBuiltInTerms = (span, index, term, urlId) => ({
    type: span.attribs.type || 'string',
    term,
    _extra: { field_name: span.attribs.term || span.attribs.field_type, url_id: urlId, uid: span.attribs.uid },
    index,
});
const parseBuiltInType = (span, index, urlId) => ({
    type: span.attribs.type,
    _extra: { field_name: span.attribs.type, url_id: urlId, uid: span.attribs.uid },
    index,
});
const parseOwner = (span, index, urlId) => ({
    type: 'owner',
    account_role: span.attribs.account_role,
    field_display_name: span.attribs.field_name,
    _extra: { url_id: urlId, uid: span.attribs.uid, field_name: span.attribs.field_name, owner_field: span.attribs.owner_field },
    index,
});
const parseHealthField = (field, index, urlId) => ({
    type: field.attribs.term ? 'string' : field.attribs.type,
    term: field.attribs.term,
    dimension: field.attribs.dimension,
    _extra: { display_type: field.attribs.display_type, format_pattern: field.attribs.format_pattern, url_id: urlId, uid: field.attribs.uid },
    index,
});
const fieldTypes = ['identifier', 'dateMetric', 'displayName', 'healthAttribute'];
const isValidFieldTag = ({ attribs }) => attribs && (attribs.field_name || attribs.q || fieldTypes.indexOf(attribs.field_type) > -1 || attribs.belongs_to === 'constant');
const buildFieldKey = (fieldName, index) => {
    if (!fieldName) {
        return undefined;
    }
    const sanitizedFieldName = fieldName.replace(/\s+/g, '_').replace(/[^a-z0-9_]/gi, '');
    return `${sanitizedFieldName}_${index}`;
};
const getFieldKey = (tag, mention, index) => {
    if (tag.attribs.uid) {
        return tag.attribs.uid;
    }
    const fieldName = tag.attribs.field_name || get(mention, '_extra.field_name', null);
    return buildFieldKey(fieldName, index);
};
const findMatchingField = (accountFields = [], userFields = [], fieldKey = undefined) => {
    let accountField = accountFields.find((a) => get(a, '_extra.uid') === fieldKey);
    let userField = userFields.find((a) => get(a, '_extra.uid') === fieldKey);
    accountField = accountField || accountFields.find((a) => buildFieldKey(a.attribute, a.index) === fieldKey || buildFieldKey(get(a, '_extra.field_name'), a.index) === fieldKey);
    userField = userField || userFields.find((a) => buildFieldKey(a.attribute, a.index) === fieldKey || buildFieldKey(get(a, '_extra.field_name'), a.index) === fieldKey);
    return accountField || userField;
};
const parseHtmlForAttributeField = (html) => {
    const $ = cheerio.load(html);
    const fieldSpans = $('field');
    const userFields = [];
    const accountFields = [];
    const fallBacks = {};
    // Fields
    const keys = Object.keys(fieldSpans);
    keys.forEach((key, index) => {
        const span = fieldSpans[key];
        if (isValidFieldTag(span)) {
            let mention = null;
            const urlId = $(span).closest('link-field').attr('id');
            if (span.attribs.field_type === 'built_in_type') {
                mention = parseBuiltInType(span, index, urlId);
            }
            if (span.attribs.field_name) {
                mention = parseAttribute(span, index, urlId);
            }
            if (span.attribs.q) {
                mention = parseMetric(span, index, urlId);
            }
            if (span.attribs.term || span.attribs.field_type === 'identifier' || span.attribs.field_type === 'displayName') {
                let term = span.attribs.field_type === 'displayName' ? 'display_name' : '';
                term = term || span.attribs.term || span.attribs.field_type;
                mention = parseBuiltInTerms(span, index, term, urlId);
            }
            if (span.attribs.field_type === 'healthAttribute') {
                mention = parseHealthField(span, index, urlId);
            }
            if (span.attribs.field_type === 'dateMetric') {
                mention = parseDateMetric(span, index, urlId);
            }
            if (span.attribs.field_type === 'owner') {
                mention = parseOwner(span, index, urlId);
            }
            if (mention) {
                if (span.attribs.belongs_to === 'account') {
                    accountFields.push(mention);
                }
                else if (span.attribs.belongs_to === 'custom_fields') {
                    userFields.push(mention);
                }
            }
            const fieldKey = getFieldKey(span, mention, index);
            if (fieldKey) {
                fallBacks[fieldKey] = span.attribs.fallback;
            }
        }
    });
    return {
        fallBacks,
        userFields,
        accountFields,
    };
};
const parseMetricChangeHtml = ($, fieldTag, fieldValue) => {
    const { parent } = fieldTag;
    if (get(parent, 'attribs.class') === 'metric-change-usage-card-view') {
        const arrowDown = $(parent).find('.point-down');
        const arrowUp = $(parent).find('.point-up');
        if (arrowUp && arrowDown) {
            if (fieldValue === 0) {
                arrowDown.remove();
                arrowUp.remove();
            }
            else if (fieldValue > 0) {
                arrowDown.remove();
            }
            else if (fieldValue < 0) {
                arrowUp.remove();
            }
            else {
                $(parent).remove();
            }
        }
    }
};
const getAttributeFieldValue = (field, formatConfig) => {
    const defaultFormat = get(formatConfig, 'numeric', '0,0.[0]a');
    const format = get(field, '_extra.format_pattern', defaultFormat);
    const displayType = get(field, '_extra.display_type');
    const { value } = field;
    const type = displayType || field.type;
    let fieldType = toLower(type);
    if (field.term === 'health') {
        fieldType = 'health';
    }
    else if (toLower(field.displayType) === 'currency') {
        fieldType = 'currency';
    }
    switch (fieldType) {
        case 'date_attribute':
        case 'simple_date_attribute':
        case 'date':
            return moment(value).format('MMMM Do, YYYY');
        case 'number':
        case 'number_metric':
        case 'number_attribute':
        case 'numeric':
            return numeral(value).format(format);
        case 'number_metric_change':
            return numeral(value).format('0.[00]%');
        case 'currency': {
            const { formatSettings } = field;
            const parsedFormatSettings = safeParsing(formatSettings)?.currency || safeParsing(formatSettings, formatSettings);
            return isNaN((Number(value))) ? value : formatCurrency(value, parsedFormatSettings, false);
        }
        case 'owner': {
            if (isString(value)) {
                return value;
            }
            const ownerField = get(field, '_extra.owner_field', '');
            const ownerUserEmail = get(field, 'value.owner_user_email', '');
            const ownerValue = get(field, 'value.owner_value', '');
            const ownerTidValue = get(field, 'value.owner_tid_value', '');
            const ownerTidEmailValue = isValidEmail(ownerTidValue) ? ownerTidValue : '';
            return ownerField === 'email' ? ownerUserEmail || ownerTidEmailValue : ownerValue;
        }
        case 'health':
            return formatHealth(value);
        default:
            return value;
    }
};
const getDefaultValue = ({ attribs = {} }) => {
    const displayType = attribs.display_type;
    switch (displayType) {
        case 'health':
            return formatHealth(null);
        default: {
            const fieldName = attribs.field_display_name || '';
            return `${fieldName}`;
        }
    }
};
const replaceHtmlWithAttributeField = (html, accountFields, userFields, fallbacks, config = {}, returnAsText = false) => {
    const { skipSpan, wrapWithHtml = false, formatConfig = {} } = config;
    const $ = cheerio.load(html, null, wrapWithHtml); // set third argument to false to avoid auto-wrapping with <html>
    const fieldSpans = $('field');
    const keys = Object.keys(fieldSpans);
    keys.forEach((key, index) => {
        const fieldTag = fieldSpans[key];
        if (isValidFieldTag(fieldTag)) {
            const fieldKey = getFieldKey(fieldTag, null, index);
            const field = findMatchingField(accountFields, userFields, fieldKey);
            const fallbackVal = fallbacks[fieldKey];
            if (fieldTag.attribs && fieldTag.attribs.belongs_to === 'constant') {
                if (fieldTag.attribs.format_type === 'constant_current_month') {
                    const month = moment(moment().month(), 'M').format(fieldTag.attribs.format_type || 'MMMM');
                    $(fieldTag).replaceWith(skipSpan ? month : `<span class="field">${month}</span>`);
                }
            }
            if (field) {
                if (field.type === 'number_metric_change') {
                    parseMetricChangeHtml($, fieldTag, field.value);
                }
                if (!isNull(field.value) && !isUndefined(field.value)) {
                    const fieldValue = getAttributeFieldValue(field, formatConfig);
                    const finalFieldValue = (fieldValue === '' && fallbackVal) ? fallbackVal : fieldValue;
                    $(fieldTag).replaceWith(skipSpan ? `${finalFieldValue}` : `<span class="field">${finalFieldValue}</span>`);
                }
                else if (fallbackVal) {
                    $(fieldTag).replaceWith(skipSpan ? `${fallbackVal}` : `<span class="field">${fallbackVal}</span>`);
                }
                else {
                    const fieldDisplayNameStr = getDefaultValue(fieldTag);
                    $(fieldTag).replaceWith(skipSpan ? fieldDisplayNameStr : `<span class="field">${fieldDisplayNameStr}</span>`);
                }
            }
            else {
                $(fieldTag).replaceWith(getDefaultValue(fieldTag));
            }
        }
    });
    const modifiedHtml = $.html().replace(/&amp;/g, '&');
    return returnAsText ? $.text() : modifiedHtml;
};
/**
 * Strip a string representing an html element from all tags
 * @param html - the element to strip the tags from
 * @returns {string}
 */
const stripHtmlElement = (html) => {
    if (isEmpty(html)) {
        return '';
    }
    return html.replaceAll('<[^>]*>', '');
};
/**
 * Replaces all <field> tags in the given HTML string with the text content of their '.attribute-value' inner component and skip html tags.
 * @param {string} html - The HTML string to process.
 * @returns {string} - The HTML text string with <field> tags replaced by their attribute display text.
 */
const getHtmlAsTextWithFieldTagsReplacedWithTagText = (html) => {
    const $ = cheerio.load(html, { encoding: 'utf8' }, false);
    const fieldSpans = $('field');
    fieldSpans.replaceWith(function getReplaceValue() {
        const fieldTag = this;
        if (isValidFieldTag(fieldTag)) {
            const fieldDisplayText = $(fieldTag).find('.attribute-value')?.text().trim();
            const replaceValue = `{${fieldDisplayText}}`;
            return replaceValue;
        }
        return '';
    });
    return $.text();
};
module.exports = {
    parseHtmlForAttributeField,
    replaceHtmlWithAttributeField,
    getAttributeFieldValue,
    stripHtmlElement,
    getHtmlAsTextWithFieldTagsReplacedWithTagText,
    replaceHtmlFieldTagWithAttrDisplayText: getHtmlAsTextWithFieldTagsReplacedWithTagText, // Alias for backward compatibility
};
//# sourceMappingURL=index.js.map